import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {GiftIcon, UserIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {EyeIcon, PaperAirplaneIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Dropdown from "components/Dropdown";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {Offer} from "types/Offer";
import DistributeOffer from "../Distribute";
import DeleteOffer from '../Delete';
import OfferDistributionStatus from "../../../types/OfferDistributionStatus";
import DotBadge from "../../../components/DotBadge";
import AudienceMemberContextMenu from "../../../components/menus/AudienceMemberContextMenu";

// Helpers ----

// Map message status to badge color
const statusColors: Record<OfferDistributionStatus, number> = {
  [OfferDistributionStatus.Pending]: DotBadge.colors.YELLOW,
  [OfferDistributionStatus.Ongoing]: DotBadge.colors.BLUE,
  [OfferDistributionStatus.Complete]: DotBadge.colors.GREEN,
  [OfferDistributionStatus.Failed]: DotBadge.colors.RED,
}

function offerCanBeDistributed(offer: Offer): boolean {
  return offer.distribution_status == null ||
    offer.distribution_status === OfferDistributionStatus.Failed ||
    offer.distribution_status === OfferDistributionStatus.Complete
}

// Component ----

function OfferList() {
  const [page, setPage] = useState<number>(1);
  const result = useQuery(['offers.all', {page}], () => ngage.offers.all(page), {keepPreviousData: true})

  const [distributingOffer, setDistributingOffer] = useState<Offer | null>(null);
  const [deletingOffer, setDeletingOffer] = useState<Offer | null>(null);

  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prefetch next and previous pages
  prefetchNearbyPages('offers.all', page, ngage.offers.all)

  // Prepare page actions
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/offers/new')}>New</Button>

  return (
    <Page
      title="Offers"
      subTitle='Voucher based discounts and incentives'
      actions={actions}
      breadcrumbs={{'Engagement': '/', 'Offers': '/offers'}}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Incentive</TableHeader>
            <TableHeader>Expires</TableHeader>
            <TableHeader>Distribution Status</TableHeader>
            <TableHeader>Last Issued</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={7}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <GiftIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No offers</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((offer, index) => (
              <TableRow key={offer.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position='left'>
                        {[
                          {name: 'View', icon: EyeIcon, onClick: () => navigate(`/offers/${offer.id}`)},
                          {
                            name: 'Edit',
                            icon: PencilIcon,
                            onClick: () => navigate(`/offers/${offer.id}/edit`),
                            disabled: offer.was_issued
                          },
                          {
                            name: 'Distribute...',
                            icon: PaperAirplaneIcon,
                            onClick: () => setDistributingOffer(offer),
                            disabled: !offerCanBeDistributed(offer)
                          },
                          {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingOffer(offer)}
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <GiftIcon className="h-5 w-5 mr-2 text-gray-500"/> {offer.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{offer.incentive}</TableData>
                <TableData>{formattedDateTime(offer.expires_at) || (offer.valid_for ? `After ${offer.valid_for.toHuman()}` : 'Never')}</TableData>
                <TableData transform={TableData.transforms.CAPITALIZE}>
                  {offer.distribution_status &&
                    <DotBadge color={statusColors[offer.distribution_status]} text={offer.distribution_status}/>
                  }

                  {!offer.distribution_status &&
                    <DotBadge color={DotBadge.colors.GRAY} text={'N/A'}/>
                  }
                </TableData>
                <TableData>{formattedDateTime(offer.last_issued_at) || 'Never'}</TableData>
                <TableData>{formattedDateTime(offer.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'View', icon: EyeIcon, onClick: () => navigate(`/offers/${offer.id}`)},
                      {
                        name: 'Edit',
                        icon: PencilIcon,
                        onClick: () => navigate(`/offers/${offer.id}/edit`),
                        disabled: offer.was_issued
                      },
                      {
                        name: 'Distribute...',
                        icon: PaperAirplaneIcon,
                        onClick: () => setDistributingOffer(offer),
                        disabled: !offerCanBeDistributed(offer)
                      },
                      {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingOffer(offer)}
                    ]}
                  </Dropdown>
                </TableData>
              </TableRow>
            ))}

        </TableBody>
      </Table>

      {/* Distribute confirmation modal */}
      <DistributeOffer
        show={!!distributingOffer}
        offer={distributingOffer}
        onCancel={() => setDistributingOffer(null)}
        onDistribute={() => setDistributingOffer(null)}
      />

      {/* Delete confirmation modal */}
      <DeleteOffer
        offer={deletingOffer}
        show={!!deletingOffer}
        onCancel={() => setDeletingOffer(null)}
        onDelete={offer => {
          navigate('/offers')
          setDeletingOffer(null)
          notify("success", "Offer Deleted", `${offer.name} has been permanently removed`)
        }}
        onDeleteFail={offer => {
          setDeletingOffer(null)
          notify("error", "Offer Not Deleted", `${offer.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default OfferList;